import { useQuery } from '@tanstack/react-query'

import { myBranchesQueryOptions } from '@/api/reference-api'
import { ErrorSurface } from '@/components/error-surface'
import { PageHeader } from '@/components/page-header'
import { RequiresWiderScreen } from '@/components/requires-wider-screen'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { ADMIN_PERMISSIONS, useAdminPermissions } from '@/features/admin/permissions'
import { formatDateTime } from '@/lib/datetime'

/**
 * Branches.
 *
 * `GET /access/me/branches` is the only branches endpoint the published contract has, and it
 * answers one question: which branches *this session* may operate in, not an institution-wide
 * register. `admin.branch.view`, `admin.branch.manage` and its countersigning code are catalogued
 * for a register this application cannot build yet — see the capability gap on the module's landing
 * page — so this screen says plainly what it is actually reading rather than dressing the reach
 * list up as something it is not.
 */
export function AdminBranchesPage() {
  const permissions = useAdminPermissions()
  const query = useQuery(myBranchesQueryOptions())

  return (
    <RequiresWiderScreen
      screen="The branch register"
      reason="A dense table read once in a while, not a screen anybody needs on a phone."
    >
      <PageHeader
        title="Branches"
        description="The branches this session can operate in. Not an institution-wide branch register — see the note below."
      />

      {!permissions.canViewBranches ? (
        <p className="text-sm text-muted">
          This needs <span className="font-mono">{ADMIN_PERMISSIONS.branchView}</span>.
        </p>
      ) : query.isPending ? (
        <Skeleton className="h-64 w-full" />
      ) : query.isError ? (
        <ErrorSurface error={query.error} onRetry={() => void query.refetch()} />
      ) : (
        <div className="space-y-3">
          {!query.data.directoryAvailable ? (
            <Alert tone="info">
              <AlertTitle>No branch has a name yet</AlertTitle>
              <AlertDescription>
                No module owns branches as records, so the server sends every branch&apos;s code and
                name as null and says so through <span className="font-mono text-xs">directoryAvailable</span>.
                Only the identifier is shown below until that changes.
              </AlertDescription>
            </Alert>
          ) : null}

          <div className="overflow-hidden rounded-card border border-border-subtle bg-surface">
            <Table>
              <TableHeader>
                <TableRow className="hover:bg-transparent">
                  <TableHead>Branch</TableHead>
                  <TableHead>Code</TableHead>
                  <TableHead>Name</TableHead>
                  <TableHead>Home branch</TableHead>
                  <TableHead>Access expires</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {query.data.items.length === 0 ? (
                  <TableRow className="hover:bg-transparent">
                    <TableCell colSpan={5} className="py-8 text-center text-sm text-muted">
                      No branch is accessible to this session.
                    </TableCell>
                  </TableRow>
                ) : (
                  query.data.items.map((branch) => (
                    <TableRow key={branch.id} className="hover:bg-transparent">
                      <TableCell className="font-mono text-xs">{branch.id}</TableCell>
                      <TableCell className="font-mono text-xs">{branch.code ?? '—'}</TableCell>
                      <TableCell>{branch.name ?? '—'}</TableCell>
                      <TableCell>
                        {branch.isHomeBranch ? <Badge tone="brand">Home</Badge> : null}
                      </TableCell>
                      <TableCell className="text-xs">
                        {branch.accessExpiresAtUtc ? formatDateTime(branch.accessExpiresAtUtc) : 'No expiry'}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>

          {query.data.canAccessAllBranches ? (
            <p className="text-xs text-muted">
              This session may act at every branch — the list above is still only the ones the
              server has actually returned, since no directory exists yet to enumerate the rest.
            </p>
          ) : null}
        </div>
      )}
    </RequiresWiderScreen>
  )
}
